/** Supported UI locales (brief D6: Arabic, English, French in V1). */
export const SUPPORTED_LOCALES = ['ar', 'en', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export type Direction = 'rtl' | 'ltr';
export type Numerals = 'latn' | 'arab';

export const DEFAULT_LOCALE: Locale = 'en';
export const LOCALE_STORAGE_KEY = 'tb.locale';

/** Language names in their own script — never flags (i18n doc §13.2). */
export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  ar: 'العربية',
  en: 'English',
  fr: 'Français',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function directionOf(locale: Locale): Direction {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/** BCP-47 tag used for Intl formatting (en → en-GB for day-month order, i18n doc §5.3). */
export function intlLocaleOf(locale: Locale): string {
  switch (locale) {
    case 'ar':
      return 'ar';
    case 'fr':
      return 'fr-FR';
    default:
      return 'en-GB';
  }
}

/**
 * Digits actually used for a locale: Eastern Arabic (٠–٩) is an option of the Arabic UI only
 * (i18n doc §4.1–4.2); Latin-script UIs always use 0–9 so bidi ordering stays intact.
 */
export function effectiveNumerals(locale: Locale, numerals: Numerals): Numerals {
  return locale === 'ar' ? numerals : 'latn';
}

/** Intl tag with an explicit numbering system (Western by default, i18n doc §4.2). */
export function intlLocaleWithNumerals(locale: Locale, numerals: Numerals): string {
  return `${intlLocaleOf(locale)}-u-nu-${effectiveNumerals(locale, numerals)}`;
}

/** Match a browser language list (e.g. ["ar-EG", "en-US"]) to a supported locale. */
export function matchBrowserLocale(languages: readonly string[]): Locale | null {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return null;
}

export interface LocaleSources {
  /** users.preferred_language of the signed-in user. */
  user?: string | null | undefined;
  /** Explicit device choice from the language switcher (localStorage tb.locale). */
  device?: string | null | undefined;
  /** navigator.languages */
  browser?: readonly string[] | undefined;
}

/** Resolution order: user setting → device choice → browser → en (i18n doc §13.1). */
export function resolveLocale(sources: LocaleSources): Locale {
  if (isLocale(sources.user)) return sources.user;
  if (isLocale(sources.device)) return sources.device;
  const fromBrowser = sources.browser ? matchBrowserLocale(sources.browser) : null;
  return fromBrowser ?? DEFAULT_LOCALE;
}

export function readDeviceLocale(): Locale | null {
  try {
    const value = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeDeviceLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage unavailable (private mode) — the choice lives for this page only */
  }
}
